import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface FAQPageProps {
  onBack: () => void;
}

interface FAQItem {
  question: string;
  answer: string;
  keywords?: string;
}

export const FAQPage: React.FC<FAQPageProps> = ({ onBack }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'What is Orcaseotools and what does it do?',
      answer: 'Orcaseotools is a professional SEO and AEO (Answer Engine Optimization) audit platform that helps businesses worldwide improve their search engine rankings. Our tools provide comprehensive website analysis, keyword research, competitor analysis, rank tracking, and SEO optimization recommendations. We analyze over 100 SEO factors including technical SEO, on-page optimization, content quality, performance metrics, and more.',
      keywords: 'what is Orcaseotools, SEO audit tool, AEO optimization'
    },
    {
      question: 'How much does Orcaseotools cost? What are the pricing plans?',
      answer: 'Orcaseotools offers flexible pricing plans to suit businesses of all sizes. We provide a free plan with 10 credits to get started. Paid plans include Weekly ($49/month), Monthly ($99/month), and Yearly ($399/year) options. Each plan includes different credit allocations and access to our full suite of SEO, AEO, and GEO optimization tools. Enterprise plans are available for larger organizations. All plans include 24/7 support and regular updates.',
      keywords: 'Orcaseotools pricing, SEO tools cost, SEO audit pricing'
    },
    {
      question: 'How accurate are the SEO audit results?',
      answer: 'Our SEO audits are highly accurate, analyzing over 100 SEO factors using advanced algorithms and AI technology. We check technical SEO issues, on-page optimization, content quality, performance metrics, mobile optimization, Core Web Vitals, and more. Our accuracy rate is 95%+ based on industry standards and validation by SEO professionals worldwide. Results are updated in real-time as we discover new SEO factors and best practices.',
      keywords: 'SEO audit accuracy, reliable SEO tools, accurate SEO analysis'
    },
    {
      question: 'What is AEO (Answer Engine Optimization) and why is it important?',
      answer: 'AEO (Answer Engine Optimization) is the practice of optimizing content for AI-powered answer engines like ChatGPT, Google Bard, and other AI search platforms. As AI search becomes more prevalent, optimizing for AEO helps your content appear in AI-generated answers, voice search results, and featured snippets. Orcaseotools includes specialized AEO optimization features to help you stay ahead of this emerging trend and capture traffic from AI-powered searches.',
      keywords: 'AEO optimization, answer engine optimization, AI search optimization'
    },
    {
      question: 'Can I use Orcaseotools for international SEO (GEO optimization)?',
      answer: 'Yes! Orcaseotools supports GEO (Geographic SEO) optimization for businesses targeting international markets. Our tools support 150+ countries and languages, allowing you to analyze and optimize your website for specific geographic regions. You can target local SEO, international SEO, and multi-language websites. Perfect for global businesses looking to expand their reach worldwide.',
      keywords: 'GEO optimization, international SEO, local SEO tools, geographic SEO'
    },
    {
      question: 'How long does an SEO audit take?',
      answer: 'A typical SEO audit takes 2-5 minutes to complete, depending on your website size and complexity. Our advanced algorithms analyze your website in real-time, checking technical SEO, on-page elements, performance metrics, and more. You\'ll see progress updates throughout the analysis, and preliminary results are available immediately. Full comprehensive reports are generated within minutes.',
      keywords: 'SEO audit time, how long SEO analysis, fast SEO tools'
    },
    {
      question: 'Do I need technical knowledge to use Orcaseotools?',
      answer: 'No technical knowledge is required! Orcaseotools is designed for both beginners and SEO professionals. Our user-friendly interface provides clear explanations for every issue found, along with step-by-step instructions on how to fix them. Each recommendation includes detailed guidance, making it easy for anyone to improve their website\'s SEO, regardless of technical expertise.',
      keywords: 'easy SEO tools, beginner SEO, user-friendly SEO software'
    },
    {
      question: 'Can I export SEO reports?',
      answer: 'Yes! Orcaseotools allows you to export comprehensive SEO reports in PDF format. These professional reports include your overall SEO score, category breakdowns, detailed issue lists, recommendations, and visual analytics. Perfect for sharing with clients, stakeholders, or your team. Reports can be customized and branded for your business needs.',
      keywords: 'export SEO report, PDF SEO report, SEO report generator'
    },
    {
      question: 'What makes Orcaseotools different from other SEO tools?',
      answer: 'Orcaseotools stands out with its comprehensive approach to SEO, AEO, and GEO optimization all in one platform. We offer faster analysis (2-5 minutes vs. hours), AI-powered insights, support for 150+ countries, affordable pricing, and 24/7 expert support. Our tools are specifically designed for worldwide markets and include cutting-edge features like AEO optimization that many competitors don\'t offer.',
      keywords: 'best SEO tools, SEO tools comparison, Orcaseotools vs competitors'
    },
    {
      question: 'Is there a free trial available?',
      answer: 'Yes! Orcaseotools offers a free plan with 10 credits to get started. No credit card required. You can run SEO audits, use keyword research tools, and explore our platform completely free. This allows you to test our tools and see the value before committing to a paid plan. Upgrade anytime to unlock more credits and advanced features.',
      keywords: 'free SEO tools, SEO audit free trial, free SEO analysis'
    },
    {
      question: 'Do you offer SEO consulting services?',
      answer: 'Yes! Orcaseotools provides professional SEO consulting services from certified SEO experts. Our consulting services include custom SEO strategy development, implementation guidance, ongoing support, and performance reviews. Perfect for businesses that want expert guidance alongside our powerful SEO tools. Contact us to discuss your consulting needs.',
      keywords: 'SEO consulting, professional SEO services, SEO expert consultation'
    },
    {
      question: 'How do I track my keyword rankings?',
      answer: 'Orcaseotools includes a powerful rank tracking feature. Simply add your target keywords, select your target location (country/city), and our system will track your rankings across major search engines. You\'ll receive alerts when rankings change, and can view historical data, competitor comparisons, and detailed ranking reports. Track unlimited keywords with paid plans.',
      keywords: 'keyword rank tracking, SERP monitoring, position tracking'
    },
    {
      question: 'Is my website data secure?',
      answer: 'Absolutely! Orcaseotools uses bank-level encryption to protect your data. We never share your website information with third parties, and all data is stored securely. We are GDPR compliant and follow industry best practices for data security. Your privacy and security are our top priorities.',
      keywords: 'SEO tools security, data protection, GDPR compliant SEO tools'
    },
    {
      question: 'Can I analyze competitor websites?',
      answer: 'Yes! Orcaseotools includes comprehensive competitor analysis tools. Enter any competitor\'s website URL to analyze their SEO strategy, backlink profile, keyword rankings, content performance, and more. Identify gaps in your SEO strategy and opportunities to outperform competitors. Compare multiple competitors side-by-side for strategic insights.',
      keywords: 'competitor SEO analysis, competitor research tools, SEO competitor audit'
    },
    {
      question: 'What support options are available?',
      answer: 'Orcaseotools provides 24/7 customer support via email and in-app messaging. Our team of SEO experts is available to help with technical questions, strategy guidance, and platform usage. Paid plans include priority support with faster response times. We also offer comprehensive documentation, video tutorials, and a knowledge base.',
      keywords: 'SEO tools support, customer service, SEO help'
    }
  ];

  useSEOMetaTags({
    title: 'Frequently Asked Questions (FAQ) - Orcaseotools SEO & AEO Tools',
    description: 'Find answers to common questions about Orcaseotools SEO, AEO, and GEO optimization tools. Learn about pricing, features, how it works, and get support for your SEO audit needs. FAQ for SEO tools.',
    keywords: 'SEO tools FAQ, SEO audit questions, AEO tools FAQ, Orcaseotools FAQ, SEO software questions, website audit FAQ, keyword research FAQ, SEO tools help, SEO audit support',
    canonicalUrl: 'https://orcaseotools.com/faq',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      })),
      "url": "https://orcaseotools.com/faq",
      "inLanguage": "en-US"
    }
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="space-y-8">
          <header className="text-center space-y-4">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Frequently Asked Questions (FAQ)
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Find answers to common questions about Orcaseotools SEO, AEO, and GEO optimization tools. Get help with pricing, features, usage, and more.
            </p>
          </header>

          <section className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-700/50 transition-colors"
                >
                  <h2 className="text-lg font-bold text-white pr-4">{faq.question}</h2>
                  {openIndex === index ? (
                    <ChevronUp size={20} className="text-indigo-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </section>

          <section className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-2xl p-8 text-center space-y-4">
            <h2 className="text-2xl font-bold">Still Have Questions?</h2>
            <p className="text-slate-300">
              Can't find the answer you're looking for? Our support team is here to help 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@orcaseotools.com"
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold transition-colors"
              >
                Contact Support
              </a>
              <a
                href="tel:+19177958308"
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-bold transition-colors"
              >
                Call Us: +1 (917) 795-8308
              </a>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
};

